import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"

import { cn } from "@productlane/lib/src/cn"

import type { ViewerImage } from "./image-viewer/image-viewer"
import { ImageViewer } from "./image-viewer/image-viewer"

const imageStyles =
	"[&_img]:!my-2 [&_img]:max-h-96 [&_img]:max-w-full [&_img]:cursor-zoom-in [&_img]:rounded [&_img]:object-contain"

interface ZoomableContentProps {
	children: ReactNode
	className?: string
}

export function ZoomableContent({ children, className }: ZoomableContentProps) {
	const ref = useRef<HTMLDivElement>(null)
	const [viewer, setViewer] = useState<{
		images: ViewerImage[]
		index: number
		originRect: DOMRect
	} | null>(null)

	useEffect(() => {
		const node = ref.current
		if (!node) return
		const onClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement
			if (target.tagName !== "IMG") return
			e.preventDefault()
			e.stopPropagation()
			const imgs = Array.from(node.querySelectorAll("img")).filter(
				(el) => el.currentSrc || el.src,
			)
			const images: ViewerImage[] = imgs.map((el) => ({
				src: el.currentSrc || el.src,
				alt: el.alt,
				name: el.alt || undefined,
			}))
			const index = imgs.indexOf(target as HTMLImageElement)
			if (index === -1) return
			setViewer({
				images,
				index,
				originRect: target.getBoundingClientRect(),
			})
		}
		node.addEventListener("click", onClick)
		return () => node.removeEventListener("click", onClick)
	}, [])

	return (
		<div ref={ref} className={cn("select-text", imageStyles, className)}>
			{children}
			{viewer && (
				<ImageViewer
					images={viewer.images}
					index={viewer.index}
					originRect={viewer.originRect}
					onIndexChange={(index) => setViewer((v) => (v ? { ...v, index } : v))}
					onClose={() => setViewer(null)}
				/>
			)}
		</div>
	)
}
